import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../components/BlogNavigation';
import Footer from '../../home/components/Footer';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://jobos.dev';

export default function OpenRouterTutorial() {
  const [scrollProgress, setScrollProgress] = useState(0);
  const [activeSection, setActiveSection] = useState('what-is');
  const [copied, setCopied] = useState(false);
  const [linkedinMsgCopied, setLinkedinMsgCopied] = useState(false);

  // SEO + Schema.org
  useEffect(() => {
    document.title = 'How to Get a Free OpenRouter API Key — Access 100+ AI Models | JobOS';

    const setMeta = (name: string, content: string, prop = false) => {
      const attr = prop ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', 'Step-by-step guide to getting a free OpenRouter API key. Access 100+ AI models including GPT-4o, Claude 3.5, and Gemini through one unified API. $1 free credit on signup.');
    setMeta('robots', 'index, follow');
    setMeta('og:title', 'How to Get a Free OpenRouter API Key — 100+ AI Models | JobOS', true);
    setMeta('og:description', 'Access GPT-4o, Claude, Gemini and 100+ AI models through a single unified API. $1 free credit, no monthly fees. Full tutorial.', true);
    setMeta('og:url', `${SITE_URL}/blog/openrouter-free-api-key`, true);
    setMeta('og:type', 'article', true);
    setMeta('og:image', 'https://public.readdy.ai/ai/img_res/2f27eeba110b14de3d586594eb855fa7.jpg', true);
    setMeta('og:image:width', '1200', true);
    setMeta('og:image:height', '630', true);
    setMeta('og:image:alt', 'How to Get a Free OpenRouter API Key — Access 100+ AI models including GPT-4o, Claude, Gemini | JobOS', true);
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', 'How to Get a Free OpenRouter API Key — 100+ AI Models | JobOS');
    setMeta('twitter:description', 'Access GPT-4o, Claude, Gemini and 100+ AI models through a single unified API. $1 free credit, no monthly fees. Full tutorial.');
    setMeta('twitter:image', 'https://readdy.ai/api/search-image?query=Abstract%20digital%20illustration%20of%20unified%20AI%20router%20hub%20concept%20with%20multiple%20glowing%20colored%20data%20streams%20in%20orange%20teal%20and%20violet%20converging%20into%20a%20single%20bright%20central%20node%20in%20dark%20space%2C%20interconnected%20light%20trails%20from%20GPT%20Claude%20Gemini%20and%20Llama%20models%20merging%20together%2C%20futuristic%20network%20orchestration%20aesthetic%2C%20clean%20minimal%20tech%20art%20representing%20multi-model%20AI%20access%20through%20one%20API%20key%2C%20no%20text%2C%20high%20quality%20digital%20illustration%2C%20dark%20navy%20background%20with%20vibrant%20accent%20lights&width=1200&height=630&seq=openrouter-og-v1&orientation=landscape');

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `${SITE_URL}/blog/openrouter-free-api-key`);

    document.querySelectorAll('script[data-schema-page="openrouter-tutorial"]').forEach(el => el.remove());

    const schemas = [
      {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: 'How to Get a Free OpenRouter API Key — Access 100+ AI Models',
        description: 'Step-by-step guide to getting a free OpenRouter API key. Access GPT-4o, Claude 3.5, Gemini and 100+ models through one unified API.',
        url: `${SITE_URL}/blog/openrouter-free-api-key`,
        datePublished: '2026-01-01',
        dateModified: '2026-03-01',
        author: { '@type': 'Organization', name: 'JobOS', url: SITE_URL },
        publisher: {
          '@type': 'Organization',
          name: 'JobOS',
          url: SITE_URL,
          logo: { '@type': 'ImageObject', url: `${SITE_URL}/og-image.png` },
        },
        mainEntityOfPage: { '@type': 'WebPage', '@id': `${SITE_URL}/blog/openrouter-free-api-key` },
        keywords: 'OpenRouter API key, free OpenRouter API, unified AI API, GPT-4o API, Claude API, multi-model AI, OpenRouter tutorial',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'HowTo',
        name: 'How to Get a Free OpenRouter API Key',
        description: 'Get a free OpenRouter API key and access 100+ AI models with $1 free credit.',
        totalTime: 'PT5M',
        step: [
          { '@type': 'HowToStep', position: 1, name: 'Sign up at OpenRouter', text: 'Go to openrouter.ai and create a free account.', url: 'https://openrouter.ai' },
          { '@type': 'HowToStep', position: 2, name: 'Generate your API key', text: 'Navigate to the API Keys section and click "Create Key".' },
          { '@type': 'HowToStep', position: 3, name: 'Test your API key', text: 'Make a test request to verify your key works correctly.' },
          { '@type': 'HowToStep', position: 4, name: 'Paste into JobOS', text: 'Go to JobOS Settings → AI Provider → OpenRouter and paste your key.' },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Blog', item: `${SITE_URL}/blog` },
          { '@type': 'ListItem', position: 3, name: 'OpenRouter Free API Key Tutorial', item: `${SITE_URL}/blog/openrouter-free-api-key` },
        ],
      },
    ];

    schemas.forEach(schema => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-schema-page', 'openrouter-tutorial');
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });

    return () => {
      document.querySelectorAll('script[data-schema-page="openrouter-tutorial"]').forEach(el => el.remove());
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight - windowHeight;
      const scrolled = window.scrollY;
      const progress = (scrolled / documentHeight) * 100;
      setScrollProgress(progress);

      const sections = ['what-is', 'prerequisites', 'step-by-step', 'models', 'rate-limits', 'troubleshooting', 'security'];
      const current = sections.find(section => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 150 && rect.bottom >= 150;
        }
        return false;
      });
      if (current) setActiveSection(current);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.scrollY - offset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <div 
        className="fixed top-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-500 via-orange-500 to-amber-600 z-50 transition-all duration-300"
        style={{ width: `${scrollProgress}%` }}
      />
      
      <Navigation />

      <article className="pt-20">
        <div 
          className="relative h-96 bg-gradient-to-br from-slate-900 via-amber-900 to-orange-900 flex items-center justify-center overflow-hidden"
        >
          <img 
            src="https://readdy.ai/api/search-image?query=Advanced%20futuristic%20AI%20neural%20network%20hub%20with%20multiple%20glowing%20amber%20and%20orange%20pathways%20converging%20into%20a%20central%20routing%20node%2C%20representing%20multi-model%20AI%20orchestration%20and%20intelligent%20request%20routing%20across%20diverse%20language%20models%2C%20featuring%20interconnected%20circuits%2C%20data%20streams%2C%20and%20a%20sophisticated%20amber-lit%20control%20center%20in%20a%20dark%20technological%20environment%20with%20holographic%20interfaces%20and%20distributed%20computing%20visualization&width=1400&height=400&seq=openrouter-hero-cover&orientation=landscape"
            alt="OpenRouter Multi-Model Hub"
            className="absolute inset-0 w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
          <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/20 backdrop-blur-sm rounded-full border border-amber-400/30 mb-6">
              <i className="ri-route-line text-amber-400 text-lg"></i>
              <span className="text-amber-300 text-sm font-medium">Tutorial Series</span>
            </div>
            <h1 className="text-5xl font-bold text-white mb-4">
              How to Get a Free OpenRouter API Key
            </h1>
            <p className="text-xl text-gray-200 mb-6">
              Access 100+ AI models through a unified API with intelligent routing
            </p>
            <div className="flex items-center justify-center gap-6 text-sm text-gray-300">
              <div className="flex items-center gap-2">
                <i className="ri-calendar-line"></i>
                <span>Updated Jan 2025</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-time-line"></i>
                <span>8 min read</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-bar-chart-line"></i>
                <span>Beginner Friendly</span>
              </div>
            </div>
          </div>
        </div>

        {/* Published / Last Updated bar */}
        <div className="w-full border-b border-gray-100 bg-white">
          <div className="max-w-7xl mx-auto px-6 py-4 flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <i className="ri-quill-pen-line text-amber-500"></i>
              <span className="font-medium text-gray-700">Published:</span>
              <time dateTime="2026-01-01" className="font-mono">January 1, 2026</time>
            </div>
            <div className="w-px h-4 bg-gray-200 hidden sm:block"></div>
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-600 text-xs font-bold">
                <i className="ri-refresh-line text-xs"></i>
                Last updated: March 2026
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 text-xs font-bold">
                <i className="ri-shield-check-line text-xs"></i>
                Pricing &amp; models verified
              </span>
            </div>
            <div className="ml-auto text-xs text-gray-400 font-mono hidden md:block">
              Data sourced from openrouter.ai/docs
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="flex gap-12">
            <aside className="hidden lg:block w-80 flex-shrink-0">
              <div className="sticky top-24 space-y-6">
                <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-6 border border-amber-200">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 flex items-center justify-center">
                      <i className="ri-lightbulb-flash-line text-amber-600 text-xl"></i>
                    </div>
                    <h3 className="font-bold text-gray-900">TL;DR</h3>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-amber-600 mt-0.5 flex-shrink-0"></i>
                      <span>Sign up at openrouter.ai</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-amber-600 mt-0.5 flex-shrink-0"></i>
                      <span>Get $1 free credit instantly</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-amber-600 mt-0.5 flex-shrink-0"></i>
                      <span>Access 100+ AI models</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-amber-600 mt-0.5 flex-shrink-0"></i>
                      <span>Pay-as-you-go pricing</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-white rounded-xl p-6 border border-gray-200">
                  <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <i className="ri-list-unordered text-amber-600"></i>
                    Table of Contents
                  </h3>
                  <nav className="space-y-2">
                    {[
                      { id: 'what-is', label: 'What is OpenRouter?' },
                      { id: 'prerequisites', label: 'Prerequisites' },
                      { id: 'step-by-step', label: 'Step-by-Step Guide' },
                      { id: 'models', label: 'Supported Models' },
                      { id: 'rate-limits', label: 'Rate Limits & Pricing' },
                      { id: 'troubleshooting', label: 'Troubleshooting' },
                      { id: 'security', label: 'Security Best Practices' }
                    ].map(item => (
                      <button
                        key={item.id}
                        onClick={() => scrollToSection(item.id)}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all whitespace-nowrap ${
                          activeSection === item.id
                            ? 'bg-amber-100 text-amber-900 font-medium'
                            : 'text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        {item.label}
                      </button>
                    ))}
                  </nav>
                </div>

                <div className="bg-gradient-to-br from-slate-900 to-amber-900 rounded-xl p-6 text-white">
                  <h3 className="font-bold mb-3 flex items-center gap-2">
                    <i className="ri-book-open-line text-amber-400"></i>
                    Tutorial Series
                  </h3>
                  <div className="space-y-2 text-sm">
                    <Link 
                      to="/blog/google-ai-studio-free-api-key"
                      className="flex items-center gap-2 p-2 rounded-lg hover:bg-white/10 transition-colors"
                    >
                      <i className="ri-google-fill text-blue-400"></i>
                      <span>Google AI Studio</span>
                    </Link>
                    <Link 
                      to="/blog/groq-free-api-key"
                      className="flex items-center gap-2 p-2 rounded-lg hover:bg-white/10 transition-colors"
                    >
                      <i className="ri-flashlight-fill text-orange-400"></i>
                      <span>Groq API</span>
                    </Link>
                    <Link 
                      to="/blog/grok-free-api-key"
                      className="flex items-center gap-2 p-2 rounded-lg hover:bg-white/10 transition-colors"
                    >
                      <i className="ri-twitter-x-fill text-purple-400"></i>
                      <span>Grok (xAI)</span>
                    </Link>
                    <div className="flex items-center gap-2 p-2 rounded-lg bg-amber-500/20 border border-amber-400/30">
                      <i className="ri-route-line text-amber-400"></i>
                      <span className="font-medium">OpenRouter</span>
                    </div>
                  </div>
                </div>
              </div>
            </aside>

            <div className="flex-1 max-w-3xl">
              <div className="prose prose-lg max-w-none">
                <section id="what-is" className="mb-12 scroll-mt-24">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center">
                      <i className="ri-question-line text-amber-600 text-2xl"></i>
                    </div>
                    What is OpenRouter?
                  </h2>
                  <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-6 border border-amber-200 mb-6">
                    <p className="text-gray-700 leading-relaxed mb-0">
                      OpenRouter is a unified API gateway that provides access to 100+ AI models from providers like OpenAI, Anthropic, Google, Meta, and more. Instead of managing multiple API keys and endpoints, you can access all models through a single interface with intelligent routing and competitive pricing.
                    </p>
                  </div>
                  <p className="text-gray-700 leading-relaxed">
                    [Placeholder: Add detailed explanation about OpenRouter's architecture, benefits, and use cases]
                  </p>
                </section>

                <section id="prerequisites" className="mb-12 scroll-mt-24">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center">
                      <i className="ri-checkbox-circle-line text-amber-600 text-2xl"></i>
                    </div>
                    Prerequisites
                  </h2>
                  <div className="bg-white rounded-xl p-6 border border-gray-200">
                    <ul className="space-y-3">
                      <li className="flex items-start gap-3">
                        <i className="ri-check-line text-amber-600 text-xl mt-0.5 flex-shrink-0"></i>
                        <span className="text-gray-700">A valid email address</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <i className="ri-check-line text-amber-600 text-xl mt-0.5 flex-shrink-0"></i>
                        <span className="text-gray-700">Basic understanding of API concepts</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <i className="ri-check-line text-amber-600 text-xl mt-0.5 flex-shrink-0"></i>
                        <span className="text-gray-700">Payment method for credits beyond free tier (optional)</span>
                      </li>
                    </ul>
                  </div>
                </section>

                <section id="step-by-step" className="mb-12 scroll-mt-24">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center">
                      <i className="ri-footprint-line text-amber-600 text-2xl"></i>
                    </div>
                    Step-by-Step Guide
                  </h2>
                  <div className="space-y-6">
                    <div className="bg-white rounded-xl p-6 border-l-4 border-amber-500 shadow-sm">
                      <div className="flex items-start gap-4">
                        <div className="w-8 h-8 flex items-center justify-center bg-amber-100 rounded-full flex-shrink-0">
                          <span className="text-amber-700 font-bold">1</span>
                        </div>
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-gray-900 mb-2">Sign Up for OpenRouter</h3>
                          <p className="text-gray-700 leading-relaxed">
                            [Placeholder: Add detailed instructions for creating an OpenRouter account]
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white rounded-xl p-6 border-l-4 border-amber-500 shadow-sm">
                      <div className="flex items-start gap-4">
                        <div className="w-8 h-8 flex items-center justify-center bg-amber-100 rounded-full flex-shrink-0">
                          <span className="text-amber-700 font-bold">2</span>
                        </div>
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-gray-900 mb-2">Generate Your API Key</h3>
                          <p className="text-gray-700 leading-relaxed">
                            [Placeholder: Add step-by-step instructions for generating API key]
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white rounded-xl p-6 border-l-4 border-amber-500 shadow-sm">
                      <div className="flex items-start gap-4">
                        <div className="w-8 h-8 flex items-center justify-center bg-amber-100 rounded-full flex-shrink-0">
                          <span className="text-amber-700 font-bold">3</span>
                        </div>
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-gray-900 mb-2">Test Your API Key</h3>
                          <p className="text-gray-700 leading-relaxed mb-4">
                            [Placeholder: Add code examples and testing instructions]
                          </p>
                          <div className="bg-slate-900 rounded-lg p-4 overflow-x-auto">
                            <code className="text-sm text-gray-300 font-mono">
                              {`// Example API call\nconst response = await fetch('https://openrouter.ai/api/v1/chat/completions', {\n  method: 'POST',\n  headers: {\n    'Authorization': 'Bearer YOUR_API_KEY',\n    'Content-Type': 'application/json'\n  },\n  body: JSON.stringify({\n    model: 'openai/gpt-3.5-turbo',\n    messages: [{ role: 'user', content: 'Hello!' }]\n  })\n});`}
                            </code>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>

                <section id="models" className="mb-12 scroll-mt-24">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center">
                      <i className="ri-apps-line text-amber-600 text-2xl"></i>
                    </div>
                    Supported Models
                  </h2>
                  <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gradient-to-r from-amber-50 to-orange-50">
                          <tr>
                            <th className="px-6 py-4 text-left text-sm font-bold text-gray-900">Provider</th>
                            <th className="px-6 py-4 text-left text-sm font-bold text-gray-900">Popular Models</th>
                            <th className="px-6 py-4 text-left text-sm font-bold text-gray-900">Use Case</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          <tr className="hover:bg-gray-50 transition-colors">
                            <td className="px-6 py-4 text-gray-900 font-medium">OpenAI</td>
                            <td className="px-6 py-4 text-gray-700">GPT-4, GPT-3.5</td>
                            <td className="px-6 py-4 text-gray-600">General purpose</td>
                          </tr>
                          <tr className="hover:bg-gray-50 transition-colors">
                            <td className="px-6 py-4 text-gray-900 font-medium">Anthropic</td>
                            <td className="px-6 py-4 text-gray-700">Claude 3</td>
                            <td className="px-6 py-4 text-gray-600">Long context</td>
                          </tr>
                          <tr className="hover:bg-gray-50 transition-colors">
                            <td className="px-6 py-4 text-gray-900 font-medium">Google</td>
                            <td className="px-6 py-4 text-gray-700">Gemini Pro</td>
                            <td className="px-6 py-4 text-gray-600">Multimodal</td>
                          </tr>
                          <tr className="hover:bg-gray-50 transition-colors">
                            <td className="px-6 py-4 text-gray-900 font-medium">Meta</td>
                            <td className="px-6 py-4 text-gray-700">Llama 2, Llama 3</td>
                            <td className="px-6 py-4 text-gray-600">Open source</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <p className="text-gray-700 leading-relaxed mt-4">
                    [Placeholder: Add comprehensive model comparison and selection guide]
                  </p>
                </section>

                <section id="rate-limits" className="mb-12 scroll-mt-24">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center">
                      <i className="ri-speed-line text-amber-600 text-2xl"></i>
                    </div>
                    Rate Limits &amp; Pricing
                  </h2>
                  <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-6 border border-amber-200">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                          <i className="ri-gift-line text-amber-600"></i>
                          Free Tier
                        </h4>
                        <ul className="space-y-2 text-sm text-gray-700">
                          <li className="flex items-start gap-2">
                            <i className="ri-check-line text-amber-600 mt-0.5"></i>
                            <span>$1 free credit on signup</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <i className="ri-check-line text-amber-600 mt-0.5"></i>
                            <span>Access to all models</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <i className="ri-check-line text-amber-600 mt-0.5"></i>
                            <span>No time limit</span>
                          </li>
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                          <i className="ri-money-dollar-circle-line text-amber-600"></i>
                          Pay-as-you-go
                        </h4>
                        <ul className="space-y-2 text-sm text-gray-700">
                          <li className="flex items-start gap-2">
                            <i className="ri-check-line text-amber-600 mt-0.5"></i>
                            <span>Competitive pricing</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <i className="ri-check-line text-amber-600 mt-0.5"></i>
                            <span>No monthly fees</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <i className="ri-check-line text-amber-600 mt-0.5"></i>
                            <span>Usage-based billing</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                  <p className="text-gray-700 leading-relaxed mt-4">
                    [Placeholder: Add detailed pricing breakdown and rate limit information]
                  </p>
                </section>

                <section id="troubleshooting" className="mb-12 scroll-mt-24">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center">
                      <i className="ri-tools-line text-amber-600 text-2xl"></i>
                    </div>
                    Troubleshooting
                  </h2>
                  <div className="space-y-4">
                    <details className="bg-white rounded-xl border border-gray-200 overflow-hidden group">
                      <summary className="px-6 py-4 cursor-pointer hover:bg-gray-50 transition-colors flex items-center justify-between">
                        <span className="font-medium text-gray-900">API key not working?</span>
                        <i className="ri-arrow-down-s-line text-gray-400 group-open:rotate-180 transition-transform"></i>
                      </summary>
                      <div className="px-6 pb-4 text-gray-700">
                        [Placeholder: Add troubleshooting steps for API key issues]
                      </div>
                    </details>
                    <details className="bg-white rounded-xl border border-gray-200 overflow-hidden group">
                      <summary className="px-6 py-4 cursor-pointer hover:bg-gray-50 transition-colors flex items-center justify-between">
                        <span className="font-medium text-gray-900">Rate limit errors?</span>
                        <i className="ri-arrow-down-s-line text-gray-400 group-open:rotate-180 transition-transform"></i>
                      </summary>
                      <div className="px-6 pb-4 text-gray-700">
                        [Placeholder: Add solutions for rate limiting issues]
                      </div>
                    </details>
                    <details className="bg-white rounded-xl border border-gray-200 overflow-hidden group">
                      <summary className="px-6 py-4 cursor-pointer hover:bg-gray-50 transition-colors flex items-center justify-between">
                        <span className="font-medium text-gray-900">Model not available?</span>
                        <i className="ri-arrow-down-s-line text-gray-400 group-open:rotate-180 transition-transform"></i>
                      </summary>
                      <div className="px-6 pb-4 text-gray-700">
                        [Placeholder: Add guidance for model availability issues]
                      </div>
                    </details>
                  </div>
                </section>

                <section id="security" className="mb-12 scroll-mt-24">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center">
                      <i className="ri-shield-check-line text-amber-600 text-2xl"></i>
                    </div>
                    Security Best Practices
                  </h2>
                  <div className="bg-red-50 rounded-xl p-6 border border-red-200 mb-6">
                    <div className="flex items-start gap-3">
                      <i className="ri-error-warning-line text-red-600 text-xl flex-shrink-0 mt-0.5"></i>
                      <div>
                        <h4 className="font-bold text-red-900 mb-2">Important Security Notice</h4>
                        <p className="text-red-800 text-sm leading-relaxed">
                          Never expose your API key in client-side code, public repositories, or browser applications. Always use environment variables and server-side implementations.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="flex items-start gap-4 p-4 bg-white rounded-xl border border-gray-200">
                      <i className="ri-lock-line text-amber-600 text-xl flex-shrink-0 mt-1"></i>
                      <div>
                        <h4 className="font-bold text-gray-900 mb-1">Use Environment Variables</h4>
                        <p className="text-gray-700 text-sm">Store API keys in .env files and never commit them to version control.</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4 p-4 bg-white rounded-xl border border-gray-200">
                      <i className="ri-server-line text-amber-600 text-xl flex-shrink-0 mt-1"></i>
                      <div>
                        <h4 className="font-bold text-gray-900 mb-1">Server-Side Only</h4>
                        <p className="text-gray-700 text-sm">Make API calls from your backend to keep keys secure.</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4 p-4 bg-white rounded-xl border border-gray-200">
                      <i className="ri-refresh-line text-amber-600 text-xl flex-shrink-0 mt-1"></i>
                      <div>
                        <h4 className="font-bold text-gray-900 mb-1">Rotate Keys Regularly</h4>
                        <p className="text-gray-700 text-sm">Generate new API keys periodically and revoke old ones.</p>
                      </div>
                    </div>
                  </div>
                </section>

                <div className="bg-gradient-to-br from-slate-900 to-amber-900 rounded-2xl p-8 text-center mb-12">
                  <div className="w-16 h-16 flex items-center justify-center bg-amber-500/20 rounded-full mx-auto mb-4">
                    <i className="ri-rocket-line text-amber-400 text-3xl"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-3">
                    Ready to Automate Your Job Applications?
                  </h3>
                  <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
                    Use your OpenRouter API key with JobOS to power intelligent job application automation with access to 100+ AI models.
                  </p>
                  <Link
                    to="/"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-amber-500 hover:bg-amber-600 text-white font-medium rounded-lg transition-colors whitespace-nowrap"
                  >
                    Get Started with JobOS
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>

                {/* Share Section */}
                <div className="flex flex-wrap items-center gap-4 mb-12 pt-8 border-t border-gray-200">
                  <span className="text-sm font-bold text-gray-500 uppercase tracking-widest">Share:</span>
                  <a
                    href="https://twitter.com/intent/tweet?text=How%20to%20Get%20a%20Free%20OpenRouter%20API%20Key%20%E2%80%94%20Full%20Tutorial&url=https://jobos.dev/blog/openrouter-free-api-key"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center text-gray-500 hover:text-white hover:bg-black transition-all cursor-pointer"
                    title="Share on X (Twitter)"
                  >
                    <i className="ri-twitter-x-fill text-lg"></i>
                  </a>
                  <div className="flex items-center gap-1.5">
                    <a
                      href="https://www.linkedin.com/sharing/share-offsite/?url=https://jobos.dev/blog/openrouter-free-api-key"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center text-gray-500 hover:text-white hover:bg-[#0077B5] transition-all cursor-pointer"
                      title="Share on LinkedIn"
                    >
                      <i className="ri-linkedin-fill text-lg"></i>
                    </a>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText('How to Get a Free OpenRouter API Key — Access 100+ AI models (GPT-4o, Claude, Gemini & more) through one unified API. Full tutorial: https://jobos.dev/blog/openrouter-free-api-key');
                        setLinkedinMsgCopied(true);
                        setTimeout(() => setLinkedinMsgCopied(false), 2500);
                      }}
                      className={`flex items-center gap-1.5 px-3 h-8 rounded-full border text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${linkedinMsgCopied ? 'bg-[#0077B5] border-[#0077B5] text-white' : 'bg-gray-100 border-gray-200 text-gray-500 hover:border-[#0077B5] hover:text-[#0077B5]'}`}
                      title="Copy message to paste into LinkedIn"
                    >
                      <i className={linkedinMsgCopied ? 'ri-check-line' : 'ri-clipboard-line'}></i>
                      {linkedinMsgCopied ? 'Copied!' : 'Copy message'}
                    </button>
                  </div>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText('https://jobos.dev/blog/openrouter-free-api-key');
                      setCopied(true);
                      setTimeout(() => setCopied(false), 2000);
                    }}
                    className={`w-10 h-10 rounded-full border flex items-center justify-center transition-all cursor-pointer ${copied ? 'bg-amber-500 border-amber-500 text-white' : 'bg-gray-100 border-gray-200 text-gray-500 hover:bg-amber-500 hover:border-amber-500 hover:text-white'}`}
                    title={copied ? 'Copied!' : 'Copy link'}
                  >
                    <i className={copied ? 'ri-check-line text-lg' : 'ri-link text-lg'}></i>
                  </button>
                </div>

                <div className="border-t border-gray-200 pt-12">
                  <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                    <i className="ri-book-open-line text-amber-600"></i>
                    Continue Learning
                  </h3>
                  <div className="grid md:grid-cols-3 gap-6">
                    <Link
                      to="/blog/google-ai-studio-free-api-key"
                      className="group bg-white rounded-xl border border-gray-200 p-6 hover:border-amber-300 hover:shadow-lg transition-all"
                    >
                      <div className="w-12 h-12 flex items-center justify-center bg-blue-100 rounded-lg mb-4 group-hover:scale-110 transition-transform">
                        <i className="ri-google-fill text-blue-600 text-2xl"></i>
                      </div>
                      <h4 className="font-bold text-gray-900 mb-2">Google AI Studio</h4>
                      <p className="text-sm text-gray-600 mb-3">Get free access to Gemini models</p>
                      <span className="text-amber-600 text-sm font-medium flex items-center gap-1">
                        Read tutorial
                        <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
                      </span>
                    </Link>

                    <Link
                      to="/blog/groq-free-api-key"
                      className="group bg-white rounded-xl border border-gray-200 p-6 hover:border-amber-300 hover:shadow-lg transition-all"
                    >
                      <div className="w-12 h-12 flex items-center justify-center bg-orange-100 rounded-lg mb-4 group-hover:scale-110 transition-transform">
                        <i className="ri-flashlight-fill text-orange-600 text-2xl"></i>
                      </div>
                      <h4 className="font-bold text-gray-900 mb-2">Groq API</h4>
                      <p className="text-sm text-gray-600 mb-3">Lightning-fast inference speeds</p>
                      <span className="text-amber-600 text-sm font-medium flex items-center gap-1">
                        Read tutorial
                        <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
                      </span>
                    </Link>

                    <Link
                      to="/blog/grok-free-api-key"
                      className="group bg-white rounded-xl border border-gray-200 p-6 hover:border-amber-300 hover:shadow-lg transition-all"
                    >
                      <div className="w-12 h-12 flex items-center justify-center bg-purple-100 rounded-lg mb-4 group-hover:scale-110 transition-transform">
                        <i className="ri-twitter-x-fill text-purple-600 text-2xl"></i>
                      </div>
                      <h4 className="font-bold text-gray-900 mb-2">Grok (xAI)</h4>
                      <p className="text-sm text-gray-600 mb-3">Real-time knowledge with X integration</p>
                      <span className="text-amber-600 text-sm font-medium flex items-center gap-1">
                        Read tutorial
                        <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
                      </span>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
}